import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { 
  Wand2, 
  BookOpen, 
  Users, 
  Clock, 
  Target, 
  CheckCircle2,
  Lightbulb,
  Save,
  FileText,
  Plus,
  Edit,
  Download,
  X,
  Trash2,
  Upload,
  Folder,
  FolderPlus,
  RefreshCw
} from "lucide-react";
import { 
  Document, 
  Packer, 
  Paragraph, 
  TextRun, 
  HeadingLevel, 
  AlignmentType, 
  UnderlineType,
  BorderStyle,
  WidthType,
  Table,
  TableRow,
  TableCell,
  ShadingType,
  convertInchesToTwip
} from "docx";

interface LessonPlan {
  id?: string;
  title: string;
  subject: string;
  grade_level: string;
  duration: number;
  standards: string[];
  objectives: string[];
  materials: string[];
  activities: LessonActivity[];
  assessment: string;
  differentiation: string;
  research_citations: string[];
  created_at?: string;
}

interface LessonActivity {
  id: string;
  name: string;
  duration: number;
  description: string;
  type: 'warm_up' | 'instruction' | 'practice' | 'assessment' | 'closure';
  grouping: 'individual' | 'pairs' | 'small_groups' | 'whole_class';
}

interface GenerationForm {
  subject: string;
  grade: string;
  standard: string;
  duration: number;
  learningStyle: string;
  specialNeeds: string;
  topic: string;
}

const SUBJECTS = [
  "English Language Arts", "Mathematics", "Science", "Social Studies",
  "Physical Education", "Art", "Music", "Technology", "Foreign Language"
];

const GRADE_LEVELS = [
  "Pre-K", "Kindergarten", "1st Grade", "2nd Grade", "3rd Grade", 
  "4th Grade", "5th Grade", "6th Grade", "7th Grade", "8th Grade",
  "9th Grade", "10th Grade", "11th Grade", "12th Grade"
];

const LEARNING_STYLES = [
  "Visual Learners", "Auditory Learners", "Kinesthetic Learners", 
  "Reading/Writing Learners", "Mixed Learning Styles"
];

export function LessonPlanBuilder() {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<LessonPlan | null>(null);
  const [savedPlans, setSavedPlans] = useState<LessonPlan[]>([]);
  const [isViewPlanOpen, setIsViewPlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<LessonPlan | null>(null);
  const [editingPlan, setEditingPlan] = useState<LessonPlan | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [portfolioFolders, setPortfolioFolders] = useState<any[]>([]);
  const [isAddToPortfolioOpen, setIsAddToPortfolioOpen] = useState(false);
  const [selectedLessonForPortfolio, setSelectedLessonForPortfolio] = useState<LessonPlan | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [documentSummaries, setDocumentSummaries] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const [form, setForm] = useState<GenerationForm>({
    subject: "",
    grade: "",
    standard: "",
    duration: 50,
    learningStyle: "",
    specialNeeds: "",
    topic: ""
  });

  useEffect(() => {
    if (user) {
      loadSavedPlans();
      loadPortfolioFolders();
    }
  }, [user]);

  const generateLessonPlan = async () => {
    if (!form.subject || !form.grade || !form.topic) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsGenerating(true);
    try {
      // Include document summaries in the generation
      const enhancedForm = {
        ...form,
        userId: user?.id,
        researchDocuments: documentSummaries.length > 0 ? documentSummaries : null
      };

      const { data, error } = await supabase.functions.invoke('generate-lesson-plan', {
        body: enhancedForm
      });

      if (error) throw error;

      setGeneratedPlan(data.lessonPlan);
      toast.success("Lesson plan generated successfully!");
    } catch (error) {
      console.error('Error generating lesson plan:', error);
      toast.error('Failed to generate lesson plan');
    } finally {
      setIsGenerating(false);
    }
  };

  const saveLessonPlan = async (plan: LessonPlan, isUpdate = false) => {
    try {
      if (isUpdate && plan.id) {
        const { data, error } = await supabase
          .from('lesson_plans')
          .update({
            title: plan.title,
            subject: plan.subject,
            grade_level: plan.grade_level,
            duration: plan.duration,
            standards: plan.standards,
            objectives: plan.objectives,
            materials: plan.materials,
            activities: plan.activities as any,
            assessment: plan.assessment,
            differentiation: plan.differentiation,
            research_citations: plan.research_citations,
            updated_at: new Date().toISOString()
          })
          .eq('id', plan.id)
          .select()
          .single();

        if (error) throw error;

        const updatedPlans = savedPlans.map(p => 
          p.id === plan.id ? { ...plan, updated_at: data.updated_at } : p
        );
        setSavedPlans(updatedPlans);
        setEditingPlan(null);
        setIsEditMode(false);
        toast.success("Lesson plan updated successfully!");
      } else {
        const { data, error } = await supabase
          .from('lesson_plans')
          .insert({
            user_id: user?.id,
            title: plan.title,
            subject: plan.subject,
            grade_level: plan.grade_level,
            duration: plan.duration,
            standards: plan.standards,
            objectives: plan.objectives,
            materials: plan.materials,
            activities: plan.activities as any,
            assessment: plan.assessment,
            differentiation: plan.differentiation,
            research_citations: plan.research_citations
          })
          .select()
          .single();

        if (error) throw error;

        setSavedPlans([...savedPlans, { 
          ...plan, 
          id: data.id, 
          created_at: data.created_at,
          activities: data.activities as unknown as LessonActivity[]
        }]);
        setGeneratedPlan(null);
        toast.success("Lesson plan saved successfully!");
      }
    } catch (error) {
      console.error('Error saving lesson plan:', error);
      toast.error('Failed to save lesson plan');
    }
  };

  const deleteLessonPlan = async (planId: string) => {
    try {
      const { error } = await supabase
        .from('lesson_plans')
        .delete()
        .eq('id', planId);

      if (error) throw error;

      setSavedPlans(savedPlans.filter(p => p.id !== planId));
      toast.success("Lesson plan deleted successfully!");
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
      toast.error('Failed to delete lesson plan');
    }
  };

  const loadSavedPlans = async () => {
    setIsRefreshing(true);
    try {
      const { data, error } = await supabase
        .from('lesson_plans')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const plansWithTypedActivities = (data || []).map(plan => ({
        ...plan,
        activities: plan.activities as unknown as LessonActivity[]
      }));
      setSavedPlans(plansWithTypedActivities);
    } catch (error) {
      console.error('Error loading lesson plans:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadPortfolioFolders = async () => {
    try {
      // First get the user's portfolio
      const { data: portfolio } = await supabase
        .from('portfolios')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (portfolio) {
        const { data: folders } = await supabase
          .from('portfolio_folders')
          .select('*')
          .eq('portfolio_id', portfolio.id)
          .order('position');

        setPortfolioFolders(folders || []);
      }
    } catch (error) {
      console.error('Error loading portfolio folders:', error);
    }
  };

  const addLessonPlanToPortfolio = async (plan: LessonPlan, folderId?: string) => {
    try {
      // Get user's portfolio
      const { data: portfolio } = await supabase
        .from('portfolios')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (!portfolio) {
        toast.error("Portfolio not found. Please create a portfolio first.");
        return;
      }

      // Add lesson plan as portfolio artifact with proper JSON serialization
      const { error } = await supabase
        .from('portfolio_artifacts')
        .insert({
          portfolio_id: portfolio.id,
          title: plan.title,
          description: `${plan.subject} - ${plan.grade_level} (${plan.duration} minutes)`,
          file_type: 'lesson_plan',
          folder_id: folderId || null,
          metadata: JSON.parse(JSON.stringify({
            lesson_plan_data: plan,
            subject: plan.subject,
            grade_level: plan.grade_level,
            duration: plan.duration
          }))
        });

      if (error) throw error;

      setIsAddToPortfolioOpen(false);
      setSelectedLessonForPortfolio(null);
      toast.success("Lesson plan added to portfolio successfully!");
    } catch (error) {
      console.error('Error adding lesson plan to portfolio:', error);
      toast.error('Failed to add lesson plan to portfolio');
    }
  };

  const handleDocumentUpload = async (files: File[]) => {
    try {
      const summaries: string[] = [];
      
      for (const file of files) {
        // Enhanced document processing
        let content = '';
        
        if (file.type === 'application/pdf') {
          // For PDFs, we'd ideally use a PDF parsing library
          // For now, prompt user that PDF processing is limited
          content = `PDF Document: ${file.name}\n[Note: PDF content extraction is limited. Key insights should be manually referenced in the lesson topic or special considerations.]`;
        } else if (file.type.includes('word') || file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
          // For Word docs, similar limitation
          content = `Word Document: ${file.name}\n[Note: Word document content extraction is limited. Key insights should be manually referenced in the lesson topic or special considerations.]`;
        } else {
          // For text files, extract content
          try {
            const text = await file.text();
            content = `Document: ${file.name}\nContent Summary: ${text.substring(0, 2000)}${text.length > 2000 ? '...\n[Content truncated for processing]' : ''}`;
          } catch {
            content = `Document: ${file.name}\n[Unable to extract text content]`;
          }
        }
        
        summaries.push(content);
      }
      
      setUploadedDocuments([...uploadedDocuments, ...files]);
      setDocumentSummaries([...documentSummaries, ...summaries]);
      
      toast.success(`${files.length} document(s) uploaded. Text files processed, PDFs/Word docs noted for reference.`);
    } catch (error) {
      console.error('Error processing documents:', error);
      toast.error('Failed to process documents');
    }
  };

  const removeDocument = (index: number) => {
    setUploadedDocuments(uploadedDocuments.filter((_, i) => i !== index));
    setDocumentSummaries(documentSummaries.filter((_, i) => i !== index));
  };

  const startEditing = (plan: LessonPlan) => {
    setEditingPlan({ ...plan });
    setIsEditMode(true);
  };

  const cancelEditing = () => {
    setEditingPlan(null);
    setIsEditMode(false);
  };

  const formatTextWithBullets = (text: string) => {
    return text.replace(/^- /gm, '• ').replace(/^\* /gm, '• ');
  };

  const downloadAsWord = async (plan: LessonPlan) => {
    setIsDownloading(true);
    try {
      const doc = new Document({
        styles: {
          paragraphStyles: [
            {
              id: "heading1",
              name: "Heading 1",
              basedOn: "Normal",
              next: "Normal",
              quickFormat: true,
              run: {
                size: 32,
                bold: true,
                color: "2E7D32",
              },
              paragraph: {
                spacing: { before: 240, after: 120 },
              },
            },
            {
              id: "heading2",
              name: "Heading 2", 
              basedOn: "Normal",
              next: "Normal",
              quickFormat: true,
              run: {
                size: 28,
                bold: true,
                color: "1976D2",
              },
              paragraph: {
                spacing: { before: 200, after: 100 },
              },
            },
            {
              id: "bodyText",
              name: "Body Text",
              basedOn: "Normal",
              run: {
                size: 22,
              },
              paragraph: {
                spacing: { before: 60, after: 60, line: 276 },
              },
            },
          ],
        },
        sections: [{
          properties: {},
          children: [
            // Header with lesson title
            new Paragraph({
              children: [
                new TextRun({
                  text: plan.title,
                  size: 36,
                  bold: true,
                  color: "1A237E",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 300 },
            }),

            // Basic Info Table
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Subject:", bold: true })] })],
                      shading: { fill: "E3F2FD" },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: plan.subject })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Grade Level:", bold: true })] })],
                      shading: { fill: "E3F2FD" },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: plan.grade_level })] })],
                    }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Duration:", bold: true })] })],
                      shading: { fill: "E3F2FD" },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: `${plan.duration} minutes` })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: "Activities:", bold: true })] })],
                      shading: { fill: "E3F2FD" },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: `${plan.activities.length} activities` })] })],
                    }),
                  ],
                }),
              ],
              margins: { top: 100, bottom: 100, left: 100, right: 100 },
            }),

            new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),

            // Standards (if any)
            ...(plan.standards.length > 0 ? [
              new Paragraph({
                children: [new TextRun({ text: "Standards Addressed", size: 28, bold: true, color: "1976D2" })],
                spacing: { before: 200, after: 100 },
              }),
              ...plan.standards.map(standard => 
                new Paragraph({
                  children: [
                    new TextRun({ text: "• ", size: 22 }),
                    new TextRun({ text: standard, size: 22 })
                  ],
                  spacing: { after: 60 },
                })
              ),
              new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),
            ] : []),

            // Learning Objectives
            new Paragraph({
              children: [new TextRun({ text: "Learning Objectives", size: 28, bold: true, color: "1976D2" })],
              spacing: { before: 200, after: 100 },
            }),
            ...plan.objectives.map(objective => 
              new Paragraph({
                children: [
                  new TextRun({ text: "• ", size: 22 }),
                  new TextRun({ text: formatTextWithBullets(objective), size: 22 })
                ],
                spacing: { after: 60 },
              })
            ),

            new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),

            // Materials
            new Paragraph({
              children: [new TextRun({ text: "Materials Needed", size: 28, bold: true, color: "1976D2" })],
              spacing: { before: 200, after: 100 },
            }),
            ...plan.materials.map(material => 
              new Paragraph({
                children: [
                  new TextRun({ text: "• ", size: 22 }),
                  new TextRun({ text: material, size: 22 })
                ],
                spacing: { after: 60 },
              })
            ),

            new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),

            // Lesson Activities
            new Paragraph({
              children: [new TextRun({ text: "Lesson Timeline & Activities", size: 28, bold: true, color: "1976D2" })],
              spacing: { before: 200, after: 100 },
            }),
            ...plan.activities.flatMap((activity, index) => [
              new Paragraph({
                children: [
                  new TextRun({ text: `${index + 1}. ${activity.name}`, size: 24, bold: true, color: "2E7D32" }),
                  new TextRun({ text: ` (${activity.duration} minutes)`, size: 20, italics: true }),
                ],
                spacing: { before: 120, after: 60 },
              }),
              new Paragraph({
                children: [new TextRun({ text: formatTextWithBullets(activity.description), size: 22 })],
                spacing: { after: 60, line: 276 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: "Type: ", size: 20, bold: true }),
                  new TextRun({ text: activity.type.replace('_', ' '), size: 20 }),
                  new TextRun({ text: " | Grouping: ", size: 20, bold: true }),
                  new TextRun({ text: activity.grouping.replace('_', ' '), size: 20 }),
                ],
                spacing: { after: 120 },
              }),
            ]),

            new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),

            // Assessment
            new Paragraph({
              children: [new TextRun({ text: "Assessment Strategy", size: 28, bold: true, color: "1976D2" })],
              spacing: { before: 200, after: 100 },
            }),
            new Paragraph({
              children: [new TextRun({ text: formatTextWithBullets(plan.assessment), size: 22 })],
              spacing: { after: 120, line: 276 },
            }),

            new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),

            // Differentiation
            new Paragraph({
              children: [new TextRun({ text: "Differentiation & Accommodations", size: 28, bold: true, color: "1976D2" })],
              spacing: { before: 200, after: 100 },
            }),
            new Paragraph({
              children: [new TextRun({ text: formatTextWithBullets(plan.differentiation), size: 22 })],
              spacing: { after: 120, line: 276 },
            }),

            // Research Citations (if any)
            ...(plan.research_citations.length > 0 ? [
              new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 200 } }),
              new Paragraph({
                children: [new TextRun({ text: "Research Foundation", size: 28, bold: true, color: "1976D2" })],
                spacing: { before: 200, after: 100 },
              }),
              new Paragraph({
                children: [new TextRun({ text: "This lesson plan incorporates research-based strategies from:", size: 22, bold: true })],
                spacing: { after: 100 },
              }),
              ...plan.research_citations.map(citation => 
                new Paragraph({
                  children: [
                    new TextRun({ text: "• ", size: 20 }),
                    new TextRun({ text: citation, size: 20, italics: true })
                  ],
                  spacing: { after: 60 },
                })
              ),
            ] : []),

            // Footer
            new Paragraph({ children: [new TextRun({ text: "" })], spacing: { after: 300 } }),
            new Paragraph({
              children: [
                new TextRun({ 
                  text: "Generated by edugogy.com", 
                  size: 18, 
                  italics: true, 
                  color: "666666" 
                })
              ],
              alignment: AlignmentType.CENTER,
            }),
          ],
        }],
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${plan.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_lesson_plan.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Lesson plan downloaded successfully!");
    } catch (error) {
      console.error('Error downloading lesson plan:', error);
      toast.error('Failed to download lesson plan');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="h-6 w-6 text-primary" />
        <h2 className="text-2xl font-bold">AI Lesson Plan Builder</h2>
        <Badge variant="secondary" className="ml-2">Research-Based</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generation Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Generate New Lesson Plan
            </CardTitle>
            <CardDescription>
              Create research-based lesson plans tailored to your students' needs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Select value={form.subject} onValueChange={(value) => setForm({ ...form, subject: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map(subject => (
                      <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="grade">Grade Level *</Label>
                <Select value={form.grade} onValueChange={(value) => setForm({ ...form, grade: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADE_LEVELS.map(grade => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="topic">Lesson Topic *</Label>
              <Input
                id="topic"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                placeholder="e.g., Photosynthesis, Fractions, American Revolution"
              />
            </div>

            <div>
              <Label htmlFor="standard">State Standard (Optional)</Label>
              <Input
                id="standard"
                value={form.standard}
                onChange={(e) => setForm({ ...form, standard: e.target.value })}
                placeholder="e.g., CCSS.MATH.CONTENT.3.NF.A.1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 50 })}
                  min="15"
                  max="180"
                />
              </div>
              <div>
                <Label htmlFor="learningStyle">Learning Style Focus</Label>
                <Select value={form.learningStyle} onValueChange={(value) => setForm({ ...form, learningStyle: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEARNING_STYLES.map(style => (
                      <SelectItem key={style} value={style}>{style}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="specialNeeds">Special Considerations</Label>
              <Textarea
                id="specialNeeds"
                value={form.specialNeeds}
                onChange={(e) => setForm({ ...form, specialNeeds: e.target.value })}
                placeholder="ELL students, special needs, advanced learners, etc."
                rows={2}
              />
            </div>

            {/* Document Upload Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                <Label>Upload Research Documents (Optional)</Label>
              </div>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                <Input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      handleDocumentUpload(files);
                    }
                  }}
                  className="mb-2"
                />
                <p className="text-xs text-muted-foreground mb-2">
                  Upload PDFs, Word documents, or text files to incorporate research into your lesson plan
                </p>
                {uploadedDocuments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Uploaded Documents:</p>
                    {uploadedDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted/50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="text-sm truncate">{doc.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDocument(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button 
              onClick={generateLessonPlan} 
              disabled={isGenerating || !form.subject || !form.grade || !form.topic}
              className="w-full"
            >
              {isGenerating ? (
                <>Generating... <Wand2 className="h-4 w-4 ml-2 animate-spin" /></>
              ) : (
                <>Generate Lesson Plan <Wand2 className="h-4 w-4 ml-2" /></>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Generated Plan Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Generated Lesson Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {generatedPlan ? (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-lg mb-2">{generatedPlan.title}</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Subject:</strong> {generatedPlan.subject}</p>
                    <p><strong>Grade:</strong> {generatedPlan.grade_level}</p>
                    <p><strong>Duration:</strong> {generatedPlan.duration} minutes</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Learning Objectives
                  </h4>
                  <ul className="text-sm space-y-1">
                    {generatedPlan.objectives.slice(0, 3).map((obj, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        {obj}
                      </li>
                    ))}
                  </ul>
                  {generatedPlan.objectives.length > 3 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      +{generatedPlan.objectives.length - 3} more objectives...
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Key Activities
                  </h4>
                  <div className="space-y-2">
                    {generatedPlan.activities.slice(0, 2).map((activity, i) => (
                      <div key={activity.id} className="text-sm p-2 bg-muted/50 rounded">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{activity.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {activity.duration} min
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{activity.description}</p>
                      </div>
                    ))}
                    {generatedPlan.activities.length > 2 && (
                      <p className="text-xs text-muted-foreground">
                        +{generatedPlan.activities.length - 2} more activities...
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={() => saveLessonPlan(generatedPlan)} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Save to Portfolio
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">View Full Plan</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{generatedPlan.title}</DialogTitle>
                      </DialogHeader>
                      <LessonPlanView plan={generatedPlan} />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Fill out the form to generate your research-based lesson plan</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Saved Plans */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>My Saved Lesson Plans</span>
            <Button 
              onClick={loadSavedPlans} 
              variant="outline" 
              size="sm" 
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {savedPlans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedPlans.map((plan) => (
                <Card key={plan.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 line-clamp-2">{plan.title}</h4>
                    <div className="text-sm text-muted-foreground space-y-1 mb-3">
                      <p>{plan.subject} • {plan.grade_level}</p>
                      <p>{plan.duration} minutes</p>
                    </div>
                     <div className="space-y-2">
                       <div className="flex gap-2">
                         <Button 
                           variant="outline" 
                           size="sm" 
                           className="flex-1"
                           onClick={() => {
                             setSelectedPlan(plan);
                             setIsViewPlanOpen(true);
                           }}
                         >
                           <FileText className="h-4 w-4 mr-1" />
                           View
                         </Button>
                         <Button 
                           variant="outline" 
                           size="sm" 
                           className="flex-1"
                           onClick={() => startEditing(plan)}
                         >
                           <Edit className="h-4 w-4 mr-1" />
                           Edit
                         </Button>
                       </div>
                       <div className="flex gap-2">
                         <Button 
                           variant="outline" 
                           size="sm" 
                           className="flex-1"
                           onClick={() => {
                             setSelectedLessonForPortfolio(plan);
                             setIsAddToPortfolioOpen(true);
                           }}
                         >
                           <FolderPlus className="h-4 w-4 mr-1" />
                           Add to Portfolio
                         </Button>
                         <AlertDialog>
                           <AlertDialogTrigger asChild>
                             <Button variant="outline" size="sm" className="flex-1">
                               <Trash2 className="h-4 w-4 mr-1" />
                               Delete
                             </Button>
                           </AlertDialogTrigger>
                           <AlertDialogContent>
                             <AlertDialogHeader>
                               <AlertDialogTitle>Delete Lesson Plan</AlertDialogTitle>
                               <AlertDialogDescription>
                                 Are you sure you want to delete "{plan.title}"? This action cannot be undone.
                               </AlertDialogDescription>
                             </AlertDialogHeader>
                             <AlertDialogFooter>
                               <AlertDialogCancel>Cancel</AlertDialogCancel>
                               <AlertDialogAction onClick={() => deleteLessonPlan(plan.id!)}>
                                 Delete
                               </AlertDialogAction>
                             </AlertDialogFooter>
                           </AlertDialogContent>
                         </AlertDialog>
                       </div>
                       <Button 
                         variant="secondary" 
                         size="sm" 
                         className="w-full"
                         onClick={() => downloadAsWord(plan)}
                         disabled={isDownloading}
                       >
                         <Download className="h-4 w-4 mr-1" />
                         {isDownloading ? "Downloading..." : "Download Word"}
                       </Button>
                     </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No saved lesson plans yet. Generate your first one above!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Plan Dialog */}
      <Dialog open={isEditMode} onOpenChange={cancelEditing}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Edit Lesson Plan</span>
              <Button variant="ghost" size="sm" onClick={cancelEditing}>
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          {editingPlan && <EditableLessonPlan plan={editingPlan} onSave={saveLessonPlan} onCancel={cancelEditing} />}
        </DialogContent>
      </Dialog>

      {/* Add to Portfolio Dialog */}
      <Dialog open={isAddToPortfolioOpen} onOpenChange={setIsAddToPortfolioOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Portfolio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Select a folder for "{selectedLessonForPortfolio?.title}":</p>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => addLessonPlanToPortfolio(selectedLessonForPortfolio!)}
              >
                <Folder className="h-4 w-4 mr-2" />
                No Folder (Root)
              </Button>
              {portfolioFolders.map((folder) => (
                <Button
                  key={folder.id}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => addLessonPlanToPortfolio(selectedLessonForPortfolio!, folder.id)}
                >
                  <Folder className="h-4 w-4 mr-2" style={{ color: folder.color }} />
                  {folder.name}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Plan Dialog */}
      <Dialog open={isViewPlanOpen} onOpenChange={setIsViewPlanOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedPlan?.title}</span>
              {selectedPlan && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => downloadAsWord(selectedPlan)}
                  disabled={isDownloading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  {isDownloading ? "Downloading..." : "Download Word"}
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedPlan && <LessonPlanView plan={selectedPlan} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LessonPlanView({ plan }: { plan: LessonPlan }) {
  const formatTextWithBullets = (text: string) => {
    return text.replace(/^- /gm, '• ').replace(/^\* /gm, '• ');
  };

  return (
    <div className="space-y-8">
      {/* Header Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg border">
        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Subject</Label>
          <p className="font-semibold text-lg mt-1">{plan.subject}</p>
        </div>
        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Grade</Label>
          <p className="font-semibold text-lg mt-1">{plan.grade_level}</p>
        </div>
        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Duration</Label>
          <p className="font-semibold text-lg mt-1">{plan.duration} minutes</p>
        </div>
        <div>
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Activities</Label>
          <p className="font-semibold text-lg mt-1">{plan.activities.length}</p>
        </div>
      </div>

      {/* Standards */}
      {plan.standards.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2">
            Standards Addressed
          </h3>
          <div className="flex flex-wrap gap-2">
            {plan.standards.map((standard, i) => (
              <Badge key={i} variant="secondary" className="text-sm px-3 py-1">
                {standard}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Learning Objectives */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2 flex items-center gap-2">
          <Target className="h-5 w-5" />
          Learning Objectives
        </h3>
        <div className="bg-muted/30 rounded-lg p-6">
          <ul className="space-y-3">
            {plan.objectives.map((objective, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-base leading-relaxed">{formatTextWithBullets(objective)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Materials */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2">
          Materials Needed
        </h3>
        <div className="bg-muted/30 rounded-lg p-6">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {plan.materials.map((material, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="h-2 w-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                <span className="text-base">{material}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Lesson Activities */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Lesson Timeline
        </h3>
        <div className="space-y-4">
          {plan.activities.map((activity, i) => (
            <div key={activity.id} className="p-6 border rounded-lg bg-gradient-to-r from-background to-muted/30 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-lg text-primary">{activity.name}</h4>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">
                    {activity.grouping.replace('_', ' ')}
                  </Badge>
                  <Badge variant="secondary" className="font-medium">
                    {activity.duration} min
                  </Badge>
                </div>
              </div>
              <p className="text-base leading-relaxed mb-4 text-muted-foreground">
                {formatTextWithBullets(activity.description)}
              </p>
              <Badge variant="outline" className="capitalize text-xs">
                {activity.type.replace('_', ' ')}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      {/* Assessment */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2">
          Assessment Strategy
        </h3>
        <div className="bg-muted/30 rounded-lg p-6">
          <p className="text-base leading-relaxed whitespace-pre-wrap">
            {formatTextWithBullets(plan.assessment)}
          </p>
        </div>
      </div>

      {/* Differentiation */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2">
          Differentiation & Accommodations
        </h3>
        <div className="bg-muted/30 rounded-lg p-6">
          <p className="text-base leading-relaxed whitespace-pre-wrap">
            {formatTextWithBullets(plan.differentiation)}
          </p>
        </div>
      </div>

      {/* Research Citations */}
      {plan.research_citations.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-primary border-b border-primary/20 pb-2">
            Research Foundation
          </h3>
          <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 p-6 rounded-lg">
            <p className="text-base font-medium mb-4 text-accent-foreground">
              This lesson plan incorporates research-based strategies from:
            </p>
            <ul className="space-y-2">
              {plan.research_citations.map((citation, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-accent mt-1">•</span>
                  <span className="italic text-muted-foreground leading-relaxed">{citation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function EditableLessonPlan({ plan, onSave, onCancel }: { 
  plan: LessonPlan; 
  onSave: (plan: LessonPlan, isUpdate: boolean) => void; 
  onCancel: () => void; 
}) {
  const [editedPlan, setEditedPlan] = useState<LessonPlan>(plan);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedPlan, true);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof LessonPlan, value: any) => {
    setEditedPlan(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field: keyof LessonPlan, index: number, value: string) => {
    const array = editedPlan[field] as string[];
    const newArray = [...array];
    newArray[index] = value;
    setEditedPlan(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field: keyof LessonPlan) => {
    const array = (editedPlan[field] as string[]) || [];
    setEditedPlan(prev => ({ ...prev, [field]: [...array, ''] }));
  };

  const removeArrayItem = (field: keyof LessonPlan, index: number) => {
    const array = editedPlan[field] as string[];
    setEditedPlan(prev => ({ ...prev, [field]: array.filter((_, i) => i !== index) }));
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Title</Label>
          <Input 
            value={editedPlan.title} 
            onChange={(e) => updateField('title', e.target.value)}
          />
        </div>
        <div>
          <Label>Subject</Label>
          <Select value={editedPlan.subject} onValueChange={(value) => updateField('subject', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUBJECTS.map(subject => (
                <SelectItem key={subject} value={subject}>{subject}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Grade Level</Label>
          <Select value={editedPlan.grade_level} onValueChange={(value) => updateField('grade_level', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRADE_LEVELS.map(grade => (
                <SelectItem key={grade} value={grade}>{grade}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Duration (minutes)</Label>
          <Input 
            type="number"
            value={editedPlan.duration} 
            onChange={(e) => updateField('duration', parseInt(e.target.value) || 0)}
          />
        </div>
      </div>

      {/* Standards */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Standards</Label>
          <Button variant="outline" size="sm" onClick={() => addArrayItem('standards')}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-2">
          {editedPlan.standards.map((standard, index) => (
            <div key={index} className="flex gap-2">
              <Input 
                value={standard} 
                onChange={(e) => updateArrayField('standards', index, e.target.value)}
                placeholder="e.g., CCSS.MATH.CONTENT.3.NF.A.1"
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => removeArrayItem('standards', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Objectives */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Learning Objectives</Label>
          <Button variant="outline" size="sm" onClick={() => addArrayItem('objectives')}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-2">
          {editedPlan.objectives.map((objective, index) => (
            <div key={index} className="flex gap-2">
              <Textarea 
                value={objective} 
                onChange={(e) => updateArrayField('objectives', index, e.target.value)}
                placeholder="Enter learning objective..."
                rows={2}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => removeArrayItem('objectives', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Materials */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Materials Needed</Label>
          <Button variant="outline" size="sm" onClick={() => addArrayItem('materials')}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-2">
          {editedPlan.materials.map((material, index) => (
            <div key={index} className="flex gap-2">
              <Input 
                value={material} 
                onChange={(e) => updateArrayField('materials', index, e.target.value)}
                placeholder="Enter material..."
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => removeArrayItem('materials', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Assessment */}
      <div>
        <Label>Assessment Strategy</Label>
        <Textarea 
          value={editedPlan.assessment} 
          onChange={(e) => updateField('assessment', e.target.value)}
          rows={4}
          placeholder="Describe how student learning will be assessed..."
        />
      </div>

      {/* Differentiation */}
      <div>
        <Label>Differentiation & Accommodations</Label>
        <Textarea 
          value={editedPlan.differentiation} 
          onChange={(e) => updateField('differentiation', e.target.value)}
          rows={4}
          placeholder="Describe accommodations for different learners..."
        />
      </div>

      {/* Research Citations */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Research Citations</Label>
          <Button variant="outline" size="sm" onClick={() => addArrayItem('research_citations')}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-2">
          {editedPlan.research_citations.map((citation, index) => (
            <div key={index} className="flex gap-2">
              <Textarea 
                value={citation} 
                onChange={(e) => updateArrayField('research_citations', index, e.target.value)}
                placeholder="Enter research citation..."
                rows={2}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => removeArrayItem('research_citations', index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} className="flex-1">
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
      </div>
    </div>
  );
}